import { MovementType, PaymentMethod, TransactionStatus, TransactionSubType, TransactionType } from '@seccl/client';
import type { ISelectOption } from '@seccl/ui';
import format from 'date-fns/format';
import subBusinessDays from 'date-fns/subBusinessDays';

import type { ILegacyTransactionFilters, ITransactionFilters } from './allTransactionsTable.types';
import { TransactionMovementEnum, TransactionSortEnum } from './allTransactionsTable.types';

export const presets = {
  PRESET_SUSPICIOUS_ACTIVITY: '1',
  PRESET_UNPAID_DEPOSITS: '2',
  PRESET_UNEXECUTED_ORDERS: '3',
} as const;

export const presetOptions = [
  { id: presets.PRESET_SUSPICIOUS_ACTIVITY, name: 'Suspicious activity' },
  { id: presets.PRESET_UNPAID_DEPOSITS, name: 'Unpaid deposits' },
  { id: presets.PRESET_UNEXECUTED_ORDERS, name: 'Unexecuted orders' },
];

export const suspiciousActivityFormState = {
  status: {
    transaction: [TransactionStatus.Checking],
  },
};

export const unpaidDepositsFormState = {
  movementType: MovementType.In,
  type: {
    transaction: TransactionType.Payment,
    transactionSubType: [TransactionSubType.Deposit, TransactionSubType.Employee, TransactionSubType.Employer],
    movement: TransactionMovementEnum.In,
    method: [PaymentMethod.BankTransfer],
  },
  status: {
    incomplete: true,
  },
};

export const unexecutedOrdersFormState = () => ({
  dateRange: {
    start: format(new Date('1900-01-01'), 'yyyy-MM-dd'),
    end: format(subBusinessDays(new Date(), 3), 'yyyy-MM-dd'),
  },
  type: {
    transaction: TransactionType.Order,
  },
  status: {
    transaction: [TransactionStatus.Pending],
  },
});

export const formStatePresets = () =>
  new Map<string, Partial<ITransactionFilters>>([
    [presets.PRESET_SUSPICIOUS_ACTIVITY, suspiciousActivityFormState],
    [presets.PRESET_UNPAID_DEPOSITS, unpaidDepositsFormState],
    [presets.PRESET_UNEXECUTED_ORDERS, unexecutedOrdersFormState()],
  ]);

/**
 * LEGACY PRESET CONSTANTS (v1 API format - string status, not array)
 *
 * These presets are used by the deprecated legacy transactions table.
 * They use single-select status format (strings) instead of multi-select (arrays).
 * This ensures correct API calls and type safety for the legacy table.
 *
 * DEPRECATION: These will be removed when 'seccl-31363-transactions-enhanced-ui'
 * is enabled for all firms and the legacy table is deprecated.
 *
 * @see emptyLegacyFormState - Corresponding empty form state for legacy table
 * @see useAllTransactionsTableLegacy - Legacy hook that uses these presets
 * @deprecated Will be removed post-v2 rollout
 */
export const suspiciousActivityLegacyFormState = {
  status: {
    transaction: TransactionStatus.Checking,
  },
};

export const unpaidDepositsLegacyFormState = {
  movementType: MovementType.In,
  type: {
    transaction: TransactionType.Payment,
    transactionSubType: [TransactionSubType.Deposit, TransactionSubType.Employee, TransactionSubType.Employer],
    movement: TransactionMovementEnum.In,
    method: [PaymentMethod.BankTransfer],
  },
  status: {
    incomplete: true,
  },
};

export const unexecutedOrdersLegacyFormState = () => ({
  dateRange: {
    start: format(new Date('1900-01-01'), 'yyyy-MM-dd'),
    end: format(subBusinessDays(new Date(), 3), 'yyyy-MM-dd'),
  },
  type: {
    transaction: TransactionType.Order,
  },
  status: {
    transaction: TransactionStatus.Pending,
  },
});

export const formStateLegacyPresets = () =>
  new Map<string, Partial<ILegacyTransactionFilters>>([
    [presets.PRESET_SUSPICIOUS_ACTIVITY, suspiciousActivityLegacyFormState],
    [presets.PRESET_UNPAID_DEPOSITS, unpaidDepositsLegacyFormState],
    [presets.PRESET_UNEXECUTED_ORDERS, unexecutedOrdersLegacyFormState()],
  ]);

export const defaultTransactionsColumnState = {
  transactions_cv: ['id', 'linkId', 'ticker', 'intendedSettlementDate', 'transactionDate'],
};

// Default column visibility states for each transaction table context
export const defaultClientTransactionsColumnState = {
  'client-transactions_cv': ['id', 'linkId', 'ticker', 'intendedSettlementDate', 'transactionDate'],
};

export const defaultAccountTransactionsColumnState = {
  'account-transactions_cv': ['id', 'linkId', 'ticker', 'intendedSettlementDate', 'transactionDate'],
};

export const DEFAULT_TRANSACTIONS_SORT = TransactionSortEnum.LastUpdatedDateDesc;

export const transactionSortFieldOptions: ISelectOption[] = [
  { id: 'createdDate', name: 'Created Date' },
  { id: 'lastStatusChangeDate', name: 'Last Updated Date' },
  { id: 'transactionDate', name: 'Transaction Date' },
  { id: 'completedDate', name: 'Completed Date' },
];

export const transactionSortOrderOptions: ISelectOption[] = [
  { id: 'desc', name: 'Newest first' },
  { id: 'asc', name: 'Oldest first' },
];

// React form doesn't allow you to reset fields to an undefined state. We use this empty form state in order to reset the filters to their default empty state.
export const emptyFormState: ITransactionFilters = {
  id: '',
  ids: '',
  linkId: '',
  clientId: '',
  accountId: '',
  assetId: '',
  movementType: '' as MovementType,
  type: {
    transaction: '' as TransactionType,
    movement: '' as TransactionMovementEnum,
    transactionSubType: [] as TransactionSubType[],
    method: [] as PaymentMethod[],
  },
  businessAdviserId: '' as unknown as number,
  status: {
    transaction: [] as TransactionStatus[],
    incomplete: null,
  },
  dateRange: {
    start: '',
    end: '',
  },
  createdDateRange: {
    start: '',
    end: '',
  },
  completedDateRange: {
    start: '',
    end: '',
  },
  lastUpdatedRange: {
    start: '',
    end: '',
  },
};

/**
 * Empty form state for the legacy transactions table.
 * The legacy table uses single-select for status (string) instead of multi-select (array).
 */
export const emptyLegacyFormState: ILegacyTransactionFilters = {
  accountId: '',
  businessAdviserId: '' as unknown as number,
  type: {
    transaction: '' as TransactionType,
    movement: '' as TransactionMovementEnum,
    transactionSubType: [] as TransactionSubType[],
    method: [] as PaymentMethod[],
  },
  status: {
    transaction: '' as TransactionStatus,
    incomplete: null,
  },
  dateRange: {
    start: '',
    end: '',
  },
};
